/* eslint-disable max-classes-per-file */
import * as Sentry from "@sentry/vue";
import type { AxiosResponse } from "axios";

import type { Comment, Entity, EntityType, Task, TaskRelationshipQuery } from "~/shared/types";
import { useAppStore, usePageStore } from "~/stores";
import { encodeArray } from "~/utils/api";

import backendOld from "./backendOld";
import { BatchManager, MAX_BATCH_SIZE } from "./batchManager";

/* A batch manager for getting entities by their duids */
class EntityListManager extends BatchManager<string, Entity<EntityType>> {
  constructor() {
    super((entityType: string) => async (duids: string[]): Promise<Map<string, Entity<EntityType>>> => {
      const response: AxiosResponse<{ results: Entity<EntityType>[]; next: string | null }> = await backendOld._get(
        `${entityType}?limit=${MAX_BATCH_SIZE}&offset=0&${encodeArray("duids", duids)}`,
        { background: true }
      );
      if (!response) {
        return new Map();
      }

      if (response.data.next !== null) {
        Sentry.captureMessage("EntityListManager: response cut off");
      }

      return new Map(response.data.results.map((entity: Entity<EntityType>) => [entity.duid, entity]));
    });
  }

  async getEntityByDuid<T extends EntityType>(entityType: T, duid: string): Promise<Entity<T> | undefined> {
    if (usePageStore().isPublicView) {
      return undefined;
    }

    return this.get(entityType, duid);
  }
}
export const entityListManager = new EntityListManager();

/* A batch manager for getting most recent comments for tasks */
class TaskCommentManager extends BatchManager<string, Comment | null> {
  constructor() {
    super(
      () =>
        async (taskDuids: string[]): Promise<Map<string, Comment | null>> => {
          const appStore = useAppStore();
          const pageStore = usePageStore();
          const publicViewDuid = pageStore.isPublicView && appStore.currentPage ? appStore.currentPage.duid : undefined;

          const response = await backendOld.tasks.getMostRecentCommentForTasks(taskDuids, publicViewDuid);
          if (!response) {
            return new Map();
          }

          return new Map(Object.entries(response.data.result));
        },
      300
    );
  }

  async getMostRecentCommentForTask(taskDuid: string): Promise<Comment | null> {
    const result = await this.get("taskComments", taskDuid);
    return result ?? null;
  }
}
export const taskCommentManager = new TaskCommentManager();

/* A batch manager for querying task relationships */
class TaskRelationshipsManager extends BatchManager<string, Task[] | null> {
  constructor() {
    super(
      () =>
        async (queryKeys: string[]): Promise<Map<string, Task[] | null>> => {
          const queries: TaskRelationshipQuery[] = queryKeys.map((key) => JSON.parse(key));

          const response = await backendOld.tasks.queryRelationships(queries);
          if (!response) {
            return new Map();
          }

          const resultMap = new Map<string, Task[] | null>();
          queries.forEach((query) => {
            const key = JSON.stringify(query);
            resultMap.set(key, response.data.result[query.duid] || null);
          });

          return resultMap;
        },
      100
    );
  }

  async queryTaskRelationships(query: TaskRelationshipQuery): Promise<Task[] | null> {
    const key = JSON.stringify(query);
    const result = await this.get("taskRelationships", key);
    return result ?? null;
  }
}
export const taskRelationshipsManager = new TaskRelationshipsManager();

export default {
  entityListManager,
  taskCommentManager,
  taskRelationshipsManager,
};
